// AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
//
// The reports page makes TWO statements about how many reports there are: the
// counter in the card header and the table's empty message. Both used to read
// straight from an array that is only filled once the Firestore read returns,
// so on arrival the page said '0 rapportages' and 'Geen rapportages gevonden.'
// while three reports were on their way. Inbox #224.
//
// Both statements now come out of this one function, so they cannot drift apart
// and the "don't know yet" branch cannot be forgotten in one of them.

import toMillis from '../measurementSignals/toMillis';

const LOADING_LABEL = 'Rapportages worden opgehaald…';

/* Aanmaakmoment: `CreatedAt`, anders `Timestamp` (op LiveReports2 na het
   aanmaken nooit meer geschreven — bewerken raakt alleen `UpdatedAt`). Geen van
   beide (2 van de 170 levende rapporten, 2026-10-21) = onderaan. */
const createdMs = (report) => toMillis(report.CreatedAt) ?? toMillis(report.Timestamp) ?? -Infinity;

/* Nieuwste bovenaan; een gelijk aanmaakmoment (19 rapporten deelden er een
   binnen hun organisatie) breekt op ID, zodat de volgorde niet wisselt met wat
   Firestore aanlevert.
   AI-CONTEXT: See docs/pitfalls.md#database-coarse-sort-key-ties-break-arbitrarily */
const newestFirst = (a, b) => {
    const diff = createdMs(b) - createdMs(a);
    if (diff !== 0 && !Number.isNaN(diff)) return diff;
    return String(a.ID || '').localeCompare(String(b.ID || ''));
};

/**
 * @typedef {object} ReportsListState
 * @property {boolean} loaded - False while the read is still in flight
 * @property {object[]} items - Live reports matching the search, newest first (empty when not loaded)
 * @property {string} countLabel - What the header says; counts ALL live reports, not the search result
 * @property {string} emptyMessage - What the table says when it has no rows
 */

/**
 * Describe what the reports list may claim right now.
 *
 * @param {object[]|null|undefined} reports - Raw hook output; anything not an array means "not loaded yet"
 * @param {string} search - Raw search box value
 * @returns {ReportsListState}
 */
const describeReportsList = (reports, search) => {
    /* Not an array = the hook has not answered yet. Saying "none" here is a
       statement to the reader, and it is untrue while the data is on its way. */
    if (!Array.isArray(reports)) {
        return { loaded: false, items: [], countLabel: LOADING_LABEL, emptyMessage: LOADING_LABEL };
    }

    const live = reports.filter(r => !r.Deleted).sort(newestFirst);
    const term = (search || '').trim().toLowerCase();
    const items = term
        ? live.filter(r => (r.Name || '').toLowerCase().includes(term))
        : live;

    return {
        loaded: true,
        items,
        /* The counter reports the whole list, never the search result: '0
           rapportages' because a search missed would read as "they are gone". */
        countLabel: `${live.length} ${live.length === 1 ? 'rapportage' : 'rapportages'}`,
        emptyMessage: term
            ? `Geen rapportages gevonden voor "${search.trim()}".`
            : 'Geen rapportages gevonden.',
    };
};

export default describeReportsList;
